// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import { getEmpresaEventosPublicadosActivos } from "@/features/empresa/empresaThunks";
import {
  seguirEmpresa,
  dejarDeSeguirEmpresa,
} from "@/features/seguidores/seguidoresThunks";

// React and Router
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";

// Animations
import { motion, AnimatePresence } from "framer-motion";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Utils
import { shareContent } from "@/utils/shareUtil";

// Components
import Portada from "@/components/Perfil/Portada";
import TicketCarousel from "@/components/TicketCarousel/TicketCarousel";
import RespaldoAppWhoop from "@/components/Perfil/RespaldoAppWhoop";
import TicketCardPerfilEmpresa from "@/components/TicketCarousel/TicketCardPerfilEmpresa";

// Assets
import GenericMap from "@/assets/Mapsicle Map.webp";
import genericUserImage from "@/assets/avatars/avatar-generic.webp";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.08, ease: "easeOut" },
  }),
};

const DIA_ABREV = {
  Lunes: "Lun", Martes: "Mar", Miércoles: "Mié", Miercoles: "Mié",
  Jueves: "Jue", Viernes: "Vie", Sábado: "Sáb", Sabado: "Sáb", Domingo: "Dom",
};

function HorarioDisplay({ tipoHorario, horarios }) {
  if (!tipoHorario || tipoHorario === "No disponible") return null;

  if (tipoHorario === "Siempre abierto") {
    return (
      <span className="inline-flex items-center gap-1.5 bg-green-500/15 text-green-400 border border-green-500/35 rounded-full px-3 py-1 text-xs font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
        Siempre abierto
      </span>
    );
  }

  if (tipoHorario === "Horario concreto") {
    let parsed = null;
    try { parsed = typeof horarios === "string" ? JSON.parse(horarios) : horarios; } catch { return null; }
    if (!parsed) return null;

    let diasAbiertos = [];
    if (Array.isArray(parsed)) {
      diasAbiertos = parsed.filter((d) => d.abierto);
    } else if (typeof parsed === "object") {
      const DIA_LABEL = { lunes: "Lunes", martes: "Martes", miercoles: "Miércoles", jueves: "Jueves", viernes: "Viernes", sabado: "Sábado", domingo: "Domingo" };
      diasAbiertos = Object.entries(parsed).filter(([, v]) => v.abierto).map(([k, v]) => ({ dia: DIA_LABEL[k] || k, horaApertura: v.horaApertura, horaCierre: v.horaCierre }));
    }

    if (diasAbiertos.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-2 mt-1">
        {diasAbiertos.map((d, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-sm text-gray-300 bg-aw-blueblack border border-gray-600 rounded-lg px-3 py-1.5">
            <span className="font-bold text-white">{DIA_ABREV[d.dia] || d.dia}</span>
            <span className="text-gray-600">·</span>
            <span className="text-aw-green font-medium">{d.horaApertura} – {d.horaCierre}</span>
          </span>
        ))}
      </div>
    );
  }

  return null;
}

export default function PerfilEmpresa() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { infoEmpresa } = useSelector((state) => state.empresa);
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [eventosActivos, setEventosActivos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(true);

  // Follow state for empresa
  const [isFollowingEmpresa, setIsFollowingEmpresa] = useState(false);
  const [followEmpresaLoading, setFollowEmpresaLoading] = useState(false);
  const [totalSeguidoresEmpresa, setTotalSeguidoresEmpresa] = useState(0);

  useEffect(() => {
    const empresaUid = infoEmpresa?.uid || id;
    if (!empresaUid) return;
    setLoadingEventos(true);
    dispatch(getEmpresaEventosPublicadosActivos({ uid: empresaUid, page: 1, limit: 10 }))
      .unwrap()
      .then((data) => { setEventosActivos(data.eventos || []); })
      .catch((error) => {
        console.error("[PerfilEmpresa] Error al cargar eventos activos:", error);
        setEventosActivos([]);
      })
      .finally(() => { setLoadingEventos(false); });
  }, [dispatch, id]);

  // Initialize follow state from infoEmpresa
  useEffect(() => {
    if (infoEmpresa) {
      if (infoEmpresa.isFollowing !== undefined) setIsFollowingEmpresa(!!infoEmpresa.isFollowing);
      if (infoEmpresa.totalSeguidores !== undefined) setTotalSeguidoresEmpresa(infoEmpresa.totalSeguidores);
    }
  }, [infoEmpresa?.uid]);

  // Follow/unfollow handler for empresa
  const handleFollowEmpresa = async () => {
    if (!isAuthenticated) return;
    setFollowEmpresaLoading(true);
    try {
      if (isFollowingEmpresa) {
        await dispatch(dejarDeSeguirEmpresa({ empresaUid: infoEmpresa.uid })).unwrap();
        setIsFollowingEmpresa(false);
        setTotalSeguidoresEmpresa((prev) => Math.max(0, prev - 1));
      } else {
        await dispatch(seguirEmpresa({ empresaUid: infoEmpresa.uid })).unwrap();
        setIsFollowingEmpresa(true);
        setTotalSeguidoresEmpresa((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error al cambiar estado de seguimiento de empresa:", error);
    } finally {
      setFollowEmpresaLoading(false);
    }
  };

  // Build redes sociales from infoEmpresa fields
  const buildRedesSociales = (info) => {
    const redes = [];
    if (info?.facebook) redes.push({ url: info.facebook, icon: "fa-brands fa-facebook" });
    if (info?.instagram) redes.push({ url: info.instagram, icon: "fa-brands fa-instagram" });
    if (info?.twitter) redes.push({ url: info.twitter, icon: "fa-brands fa-x-twitter" });
    if (info?.tiktok) redes.push({ url: info.tiktok, icon: "fa-brands fa-tiktok" });
    if (info?.youtube) redes.push({ url: info.youtube, icon: "fa-brands fa-youtube" });
    if (info?.twitch) redes.push({ url: info.twitch, icon: "fa-brands fa-twitch" });
    if (info?.spotify) redes.push({ url: info.spotify, icon: "fa-brands fa-spotify" });
    if (info?.linkedin) redes.push({ url: info.linkedin, icon: "fa-brands fa-linkedin" });
    if (info?.whatsapp) redes.push({ url: info.whatsapp, icon: "fa-brands fa-whatsapp" });
    if (info?.website) redes.push({ url: info.website, icon: "fa-solid fa-globe" });
    return redes;
  };

  // Build ubicacion text
  const buildUbicacionText = (info) => {
    const parts = [info?.direccion, info?.comuna, info?.ciudad, info?.pais].filter(Boolean);
    return parts.join(", ") || "Ubicación no disponible";
  };

  // Share handler
  const handleSharePerfil = async () => {
    const perfilUrl = `${window.location.origin}/empresa/${infoEmpresa?.alias || infoEmpresa?.uid}`;
    await shareContent({
      url: perfilUrl,
      title: infoEmpresa?.nombre || "Perfil de Empresa",
      text: `¡Conoce ${infoEmpresa?.nombre}!`,
      successMessage: "¡Link del perfil copiado al portapapeles!",
    });
  };


  const redesSociales = buildRedesSociales(infoEmpresa);
  const ubicacionText = buildUbicacionText(infoEmpresa);
  const hasHorario = infoEmpresa?.tipoHorario && infoEmpresa?.tipoHorario !== "No disponible";
  const hasUbicacion = infoEmpresa?.siubicacionfisica !== 0 && infoEmpresa?.direccion;

  const activeProfile = infoEmpresa?.published ?? 0;
  const badgeColor =
    activeProfile === 1 ? "text-aw-green" :
    activeProfile === 2 ? "text-yellow-500" :
    activeProfile === 3 ? "text-red-500" :
    "text-gray-500";
  const badgeLabel =
    activeProfile === 1 ? "Empresa verificada" :
    activeProfile === 2 ? "Verificación en proceso" :
    activeProfile === 3 ? "Perfil rechazado" :
    "Perfil no verificado";

  return (
    <>
      {/* PORTADA */}
      <div className="w-full max-w-5xl mx-auto relative">
        <Portada info={infoEmpresa} />
      </div>

      {/* HERO SECTION — avatar, nombre, stats, acciones */}
      <div className="w-full min-h-fit rounded-t-[8%] bg-gradient-to-b from-aw-bluedark from-20% via-aw-bluedark-85 via-70% to-aw-bluedark-30 to-100% flex flex-col items-center pt-20 pb-8 -mt-20 sm:-mt-[5.5rem]">
        <div className="w-full max-w-5xl mx-auto px-4">

          {/* Avatar + nombre + acciones */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">

            {/* Avatar */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="relative shrink-0"
            >
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full ring-4 ring-aw-green ring-offset-2 ring-offset-aw-bluedark overflow-hidden shadow-xl shadow-black/40">
                <img
                  src={infoEmpresa?.imgPerfil || genericUserImage}
                  alt={infoEmpresa?.nombre || "Empresa"}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Nombre, badge, categorías, ubicación inline */}
            <motion.div
              custom={1}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="flex-1 min-w-0 pb-1"
            >
              <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight truncate">
                {infoEmpresa?.nombre || "Empresa"}
              </h1>

              {/* Badge verificación */}
              {infoEmpresa?.published !== undefined && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <FontAwesomeIcon icon="fa-solid fa-certificate" className={`${badgeColor} text-sm`} />
                  <span className={`${badgeColor} text-xs font-medium`}>{badgeLabel}</span>
                </div>
              )}

              {/* Alias */}
              {(infoEmpresa?.alias || infoEmpresa?.nombre) && (
                <p className="text-gray-400 text-sm mt-0.5">
                  @{infoEmpresa?.alias || infoEmpresa?.nombre}
                </p>
              )}

              {/* Categorías */}
              {infoEmpresa?.categorias?.length > 0 && (
                <div className="flex items-center gap-1.5 mt-1">
                  <FontAwesomeIcon icon="fa-solid fa-icons" className="text-aw-green text-xs" />
                  <span className="text-gray-300 text-xs">{infoEmpresa.categorias.join(", ")}</span>
                </div>
              )}

              {/* Ubicación inline si hay */}
              {infoEmpresa?.ciudad && (
                <div className="flex items-center gap-1.5 mt-1">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-aw-green text-xs" />
                  <span className="text-gray-300 text-xs">
                    {[infoEmpresa.direccion, infoEmpresa.comuna, infoEmpresa.ciudad, infoEmpresa.pais].filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Acciones: follow + compartir */}
            <motion.div
              custom={2}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="flex flex-col items-end gap-3 shrink-0 pb-1"
            >
              <div className="flex items-center gap-2">
                {/* Botón seguir */}
                {isAuthenticated && (
                  <AnimatePresence mode="wait">
                    <motion.button
                      key={isFollowingEmpresa ? "siguiendo" : "seguir"}
                      onClick={handleFollowEmpresa}
                      disabled={followEmpresaLoading}
                      whileTap={{ scale: 0.93 }}
                      className={`px-5 py-2 rounded-lg font-semibold text-sm transition-all border ${
                        isFollowingEmpresa
                          ? "bg-transparent border-aw-green text-aw-green hover:bg-aw-green/10"
                          : "bg-aw-green border-aw-green text-aw-bluedark hover:brightness-110"
                      } ${followEmpresaLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {followEmpresaLoading ? (
                        <i className="fas fa-spinner fa-spin mr-1" />
                      ) : (
                        <FontAwesomeIcon
                          icon={isFollowingEmpresa ? "fa-solid fa-user-check" : "fa-solid fa-user-plus"}
                          className="mr-2"
                        />
                      )}
                      {isFollowingEmpresa ? "Siguiendo" : "Seguir"}
                    </motion.button>
                  </AnimatePresence>
                )}

                {/* Botón compartir */}
                <motion.button
                  whileTap={{ scale: 0.93 }}
                  onClick={handleSharePerfil}
                  className="px-4 py-2 rounded-lg font-semibold text-sm border border-gray-600 text-gray-300 hover:border-aw-green hover:text-aw-green transition-colors"
                >
                  <FontAwesomeIcon icon="fa-solid fa-share" className="mr-1.5" />
                  Compartir
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Stats row: seguidores destacado + eventos */}
          <motion.div
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex items-stretch gap-3 mt-5"
          >
            {/* Seguidores — card destacada */}
            <div className="flex items-center gap-3 bg-aw-green/10 border border-aw-green/30 rounded-xl px-5 py-3">
              <FontAwesomeIcon icon="fa-solid fa-users" className="text-aw-green text-lg" />
              <div>
                <span className="text-white font-bold text-2xl leading-none block">{totalSeguidoresEmpresa}</span>
                <span className="text-aw-green text-xs font-semibold uppercase tracking-wide">{totalSeguidoresEmpresa === 1 ? "Seguidor" : "Seguidores"}</span>
              </div>
            </div>
            {/* Eventos */}
            {eventosActivos.length > 0 && (
              <div className="flex items-center gap-3 bg-aw-bluedark border border-gray-700 rounded-xl px-5 py-3">
                <FontAwesomeIcon icon="fa-solid fa-calendar-days" className="text-gray-400 text-lg" />
                <div>
                  <span className="text-white font-bold text-2xl leading-none block">{eventosActivos.length}</span>
                  <span className="text-gray-400 text-xs font-semibold uppercase tracking-wide">Eventos activos</span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Descripción */}
          {infoEmpresa?.descripcion && (
            <motion.p
              custom={4}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="text-gray-300 text-sm mt-4 leading-relaxed max-w-2xl"
            >
              {infoEmpresa.descripcion}
            </motion.p>
          )}

          {/* Horario */}
          {(hasHorario || infoEmpresa?.horario) && (
            <motion.div
              custom={5}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mt-5 bg-aw-bluedark border border-gray-700 rounded-xl px-4 py-3 inline-flex flex-col gap-2 max-w-full"
            >
              <div className="flex items-center gap-2 text-gray-400 text-xs font-semibold uppercase tracking-wide">
                <FontAwesomeIcon icon="fa-solid fa-clock" className="text-aw-green" />
                Horario
              </div>
              {hasHorario ? (
                <HorarioDisplay tipoHorario={infoEmpresa.tipoHorario} horarios={infoEmpresa.horarios} />
              ) : (
                <span className="text-gray-300 text-sm">{infoEmpresa.horario}</span>
              )}
            </motion.div>
          )}

          {/* Redes sociales */}
          {redesSociales.length > 0 && (
            <motion.div
              custom={6}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="flex flex-wrap gap-2 mt-5"
            >
              {redesSociales.map((rrss, index) => (
                <motion.a
                  key={index}
                  href={rrss.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.12 }}
                  whileTap={{ scale: 0.93 }}
                  className="w-10 h-10 rounded-full bg-aw-bluedark border border-gray-600 hover:border-aw-green hover:bg-aw-green/10 flex items-center justify-center text-white transition-colors shadow-md"
                >
                  <FontAwesomeIcon icon={rrss.icon} className="text-base" />
                </motion.a>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="w-full bg-aw-bluedark-30 flex flex-col items-center pt-6">

        {/* Sección: Próximos Eventos */}
        <motion.div
          custom={0}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="w-full max-w-5xl mx-auto px-4 mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-6 bg-aw-green rounded-full" />
            <h3 className="text-xl font-semibold text-white">Próximos Eventos</h3>
          </div>
          {loadingEventos ? (
            <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
              <div className="w-full flex flex-row rounded-xl gap-x-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="min-w-[200px] max-w-[200px]">
                    <Skeleton height={380} className="rounded-xl" />
                  </div>
                ))}
              </div>
            </SkeletonTheme>
          ) : eventosActivos.length > 0 ? (
            <TicketCarousel
              tickets={eventosActivos}
              TicketItem={TicketCardPerfilEmpresa}
              idEmpresa={infoEmpresa?.uid}
              additionalProps={{ myself: false }}
              sectionType="eventosActivos"
              uid={infoEmpresa?.uid || id}
              onLoadMore={(newEventos) => setEventosActivos((prev) => [...prev, ...newEventos])}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 bg-aw-bluedark rounded-xl border border-gray-800">
              <FontAwesomeIcon icon="fa-solid fa-calendar-xmark" className="text-4xl mb-3 opacity-30" />
              <p className="text-sm">No hay eventos activos por ahora</p>
            </div>
          )}
        </motion.div>

        {/* Sección: Ubicación */}
        {hasUbicacion && (
          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="w-full max-w-5xl mx-auto px-4 mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-aw-green rounded-full" />
              <h3 className="text-xl font-semibold text-white">Ubicación</h3>
            </div>

            {/* Card glassmorphism de ubicación */}
            <div className="rounded-2xl overflow-hidden border border-gray-700 bg-aw-bluedark/60 backdrop-blur-sm shadow-xl">
              <div className="relative w-full h-36 sm:h-40">
                <img
                  src={GenericMap}
                  alt="Mapa de ubicación"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-aw-bluedark/50 flex flex-col items-center justify-center gap-1 px-4">
                  <div className="flex items-center gap-2 bg-aw-blueblack/80 rounded-full px-4 py-1.5 backdrop-blur-sm">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-400 text-sm" />
                    <span className="text-white text-sm font-medium line-clamp-1">{ubicacionText}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 p-3">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ubicacionText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center py-2 rounded-lg border border-gray-600 text-gray-300 text-sm font-semibold hover:bg-aw-green hover:border-aw-green hover:text-aw-bluedark transition-colors"
                >
                  Google Maps
                </a>
                <a
                  href={`https://waze.com/ul?q=${encodeURIComponent(ubicacionText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center py-2 rounded-lg border border-gray-600 text-gray-300 text-sm font-semibold hover:bg-aw-green hover:border-aw-green hover:text-aw-bluedark transition-colors"
                >
                  Ir por Waze
                </a>
              </div>
            </div>
          </motion.div>
        )}

        {/* Respaldo AppWhoop */}
        <div className="w-full max-w-5xl px-4 mb-6">
          <RespaldoAppWhoop />
        </div>
      </div>
    </>
  );
}
